/**
 * InlineConfirmationModal — shared 3-button unsaved-changes modal.
 *
 * Used by InlineEditContext (switch-edit warning) and InlineScrollAwayGuard
 * (scroll-away warning). Centralises the modal implementation to prevent
 * silent divergence between the two usage sites. Composes the shared Dialog
 * slot API — Dialog owns the header (title + description) and the sticky
 * footer; the body is empty by design.
 */

import { useCallback, useMemo } from 'react';
import {
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';

import { useStackingZIndex } from '@/contexts/StackingContext';
import { Button } from '@/lib/base/buttons/button';
import { Dialog } from '@/lib/base/overlays/modal';
import {
  MODAL_OVERLAY_ANIM,
  MODAL_PANEL_ANIM,
} from '@/lib/base/overlays/modal-animation';

export interface InlineConfirmationModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  /** Label for the primary confirm button. Defaults to "Save". Hidden when onConfirm is omitted. */
  confirmLabel?: string;
  /** Whether the confirm action is in progress (disables all buttons, shows spinner). */
  isSaving?: boolean;
  /** Primary action (e.g. Save). Omit to render a 2-button modal (cancel + discard only). */
  onConfirm?: () => void;
  onDiscard: () => void;
  /** Called when the user dismisses the modal without taking action ("Keep editing"). */
  onCancel: () => void;
}

const OVERLAY_CLASS = `fixed inset-0 flex items-center justify-center bg-black/50 ${MODAL_OVERLAY_ANIM}`;

const MODAL_CLASS = `relative max-w-lg w-full mx-4 bg-bg-primary rounded-2xl shadow-2xl ${MODAL_PANEL_ANIM}`;

const FOOTER_BUTTONS_CLASS = 'flex items-center justify-end gap-3';

export function InlineConfirmationModal({
  isOpen,
  title,
  description,
  confirmLabel = 'Save',
  isSaving = false,
  onConfirm,
  onDiscard,
  onCancel,
}: InlineConfirmationModalProps) {
  const zIndex = useStackingZIndex();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onCancel();
      }
    },
    [onCancel]
  );

  const overlayStyle = useMemo(() => ({ zIndex }), [zIndex]);
  const modalStyle = useMemo(() => ({ zIndex: zIndex + 10 }), [zIndex]);

  const footer = useMemo(
    () => (
      <div className={FOOTER_BUTTONS_CLASS}>
        <Button
          color='secondary'
          size='md'
          onClick={onCancel}
          isDisabled={isSaving}
          autoFocus={!onConfirm}
        >
          Keep editing
        </Button>
        <Button
          color='secondary'
          size='md'
          onClick={onDiscard}
          isDisabled={isSaving}
        >
          Discard
        </Button>
        {onConfirm && (
          <Button
            color='primary'
            size='md'
            onClick={onConfirm}
            isDisabled={isSaving}
            isLoading={isSaving}
            autoFocus
          >
            {confirmLabel}
          </Button>
        )}
      </div>
    ),
    [confirmLabel, isSaving, onCancel, onConfirm, onDiscard]
  );

  return (
    <AriaModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={!isSaving}
      className={OVERLAY_CLASS}
      style={overlayStyle}
    >
      <AriaModal className={MODAL_CLASS} style={modalStyle}>
        <Dialog
          hideCloseButton
          title={title}
          description={description}
          footer={footer}
        >
          {/* No body content for this modal — the title+description in the
              header are the entire surface; actions live in the footer. */}
          {null}
        </Dialog>
      </AriaModal>
    </AriaModalOverlay>
  );
}
